import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCreateKitAssetsMutation } from '../../../../redux/api/fymAPI';
import { CatalogItem, CreateKitAssetItem, SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { aiReelStudioParams } from '../studioRoute';
import { SerializedReel } from '../types';
import { usePostKitAsset } from '../library/usePostKitAsset';
import { kitRefusal } from '../releases/assets/kitErrors';
import { studioMessages } from '../studio/studioMessages';
import { itemsCredits } from '../studio/studioRules';
import { useVideoCreditGate } from '../credits/useVideoCreditGate';
import { StudioActionFeedback, studioActionErrorFeedback } from '../studio/studioActionErrorText';

/** The answer of a failed "Try again" of one kit asset; the row (or the result) shows it under its button. */
export interface StudioRetryFeedback {
  assetUuid: string;
  feedback: StudioActionFeedback;
}

interface UseStudioWorkActionsProps {
  accountId: number;
  item: CatalogItem;
  /** Opens the result view (design n5) of a ready kit asset in the Studio. */
  onOpenAsset: (asset: SerializedKitAsset) => void;
  /** "Try again" of a failed reel (the reel actions of the Studio). */
  onRetryReel: (reel: SerializedReel) => void;
  /** "Change the song" of a reel whose song cannot be used. */
  onChangeReelSong: (reel: SerializedReel) => void;
}

/**
 * The buttons of the strip rows (design release-kit r2):
 * - Open: a kit asset opens its result in the Studio; a reel opens its review page.
 * - Post it: the Composer with the file, as on the result page.
 * - Try again: a kit asset posts the same item again (`batch: false`); the server gave the credits
 *   back when it failed. A balance (plan plus packs) below the cost opens the credit packs at
 *   once and sends nothing; a 402 opens the same packs (`customBaseQuery`). Any other answer
 *   goes into `retryFeedback`, which shows under the Try again button of that asset: a 409 says
 *   "This is already being made."; an `M24_*` refusal its server sentence; another error the
 *   general sentence (the raw text goes only to the console and Sentry). A reel uses the Try again
 *   of the reel page.
 * - Open the catalog: a master that cannot be used is replaced in the catalog.
 */
export function useStudioWorkActions(props: UseStudioWorkActionsProps) {
  const { accountId, item, onOpenAsset, onRetryReel, onChangeReelSong } = props;
  const intl = useIntl();
  const router = useSMLRouter();
  const postAsset = usePostKitAsset(item);
  const [createKitAssets] = useCreateKitAssetsMutation();
  const { canSpend } = useVideoCreditGate(accountId);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryFeedback, setRetryFeedback] = useState<StudioRetryFeedback | null>(null);

  const openReel = useCallback(
    (reel: SerializedReel) =>
      router.push(SMLRoutes.promoteStudio, aiReelStudioParams({ catalogItemUuid: item.uuid, reel: reel.uuid })),
    [item.uuid, router]
  );

  const openCatalog = useCallback(() => router.push(SMLRoutes.catalog), [router]);

  const retryAsset = useCallback(
    async (asset: SerializedKitAsset): Promise<void> => {
      const items: CreateKitAssetItem[] = [{ themeId: asset.themeId, params: asset.params }];
      if (!canSpend(itemsCredits(items))) {
        return;
      }
      setRetryFeedback(null);
      setRetrying(asset.uuid);
      const result = await createKitAssets({ accountId, catalogItemUuid: item.uuid, items, batch: false });
      setRetrying(null);
      const busy: StudioRetryFeedback = {
        assetUuid: asset.uuid,
        feedback: { tone: 'info', message: intl.formatMessage(studioMessages.busy) },
      };
      if (isSuccessfulResult(result)) {
        if (result.data.created.length === 0) {
          setRetryFeedback(busy);
        }
        return;
      }
      const refusal = kitRefusal(result.error);
      if (refusal === 'videoCredits') {
        return;
      }
      if (refusal === 'busy') {
        setRetryFeedback(busy);
        return;
      }
      setRetryFeedback({
        assetUuid: asset.uuid,
        feedback: studioActionErrorFeedback(intl, 'try the asset again', result.error),
      });
    },
    [accountId, canSpend, createKitAssets, intl, item.uuid]
  );

  return useMemo(
    () => ({
      openAsset: onOpenAsset,
      openReel,
      postAsset,
      openCatalog,
      retryAsset: (asset: SerializedKitAsset) => {
        retryAsset(asset).catch(console.error);
      },
      retryReel: onRetryReel,
      changeReelSong: onChangeReelSong,
      retrying,
      retryFeedback,
    }),
    [onChangeReelSong, onOpenAsset, onRetryReel, openCatalog, openReel, postAsset, retryAsset, retrying, retryFeedback]
  );
}

export type StudioWorkActions = ReturnType<typeof useStudioWorkActions>;
